import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Context that exposes a single async `confirm({ title, description, confirmLabel, cancelLabel, destructive })`
// that resolves to true (confirmed) or false (cancelled / dismissed).
//
// Use this instead of window.confirm() — `window.confirm` is silently blocked on many mobile browsers
// and in-app webviews. This provider renders one global Shadcn AlertDialog and keeps your code
// async-friendly:
//
//   const confirm = useConfirm();
//   if (!(await confirm({ title: 'Delete dealer?', destructive: true }))) return;
//   await api.delete(...);
const ConfirmContext = createContext(null);

const DEFAULT_OPTS = {
  title: 'Are you sure?',
  description: '',
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  destructive: false,
};

export const ConfirmProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState(DEFAULT_OPTS);
  const [busy, setBusy] = useState(false);
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    setOpts({ ...DEFAULT_OPTS, ...options });
    setBusy(false);
    setOpen(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const close = (result) => {
    setOpen(false);
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  };

  const handleConfirm = async () => {
    setBusy(true);
    close(true);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(o) => { if (!o) close(false); }}>
        <AlertDialogContent
          data-testid="global-confirm-dialog"
          onKeyDown={(e) => {
            // Enter → confirm (when not focused on Cancel button); Esc handled natively by Radix
            if (e.key === 'Enter') {
              const tag = (e.target?.tagName || '').toUpperCase();
              const isCancelBtn = e.target?.getAttribute?.('data-testid') === 'global-confirm-cancel-btn';
              if (tag !== 'INPUT' && tag !== 'TEXTAREA' && !isCancelBtn && !busy) {
                e.preventDefault();
                handleConfirm();
              }
            }
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="global-confirm-title">{opts.title}</AlertDialogTitle>
            {opts.description && (
              <AlertDialogDescription data-testid="global-confirm-description">{opts.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="global-confirm-cancel-btn">{opts.cancelLabel}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={busy}
              autoFocus
              className={opts.destructive ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
              data-testid="global-confirm-ok-btn"
            >
              {opts.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
          <p className="text-[10px] text-center -mt-1 px-4 pb-1" style={{ color: '#9CA3AF' }}>
            Press <kbd className="px-1 rounded font-mono border" style={{ borderColor: '#E5E7EB', background: 'white' }}>Enter</kbd> to confirm · <kbd className="px-1 rounded font-mono border" style={{ borderColor: '#E5E7EB', background: 'white' }}>Esc</kbd> to cancel
          </p>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm() must be used inside <ConfirmProvider>');
  return ctx;
};
